/**
 * @file PrivacyManager.js
 * @fileOverview ReactJS storage utility
 * @author Smartmatic
 * @creationDate 2019-02-07
 * @modificationDate 2025-03-14
 */
// selectedPrivacy is stored in localStorage too but is handled separately
const LOCAL_STORAGE_FIELDS = {
  selectedCandidates: true,
  writeInValues: true,
};

const MILLISECONDS_IN_WEEK = 604800000; // 1000ms * 60sec * 60min * 24hr * 7d

const THREE_SECONDS = 3000; // 1000ms * 3sec

/**
 * Retrieves the user's privacy setting from the session or local storage.
 *
 * This function first checks the session storage for the 'selectedPrivacy' key. If it doesn't
 * find it there, it checks the local storage. If it finds a saved value, it parses it from JSON
 * format. If it doesn't find a saved value, it returns undefined to reflect the absence of saved
 * privacy settings. It also takes care to handle cases where a parse attempt of an undefined value
 * would cause an error.
 *
 * @returns {boolean|undefined} - The parsed privacy setting if found, or undefined if not found.
 */
export const getPrivacy = () => {
  const saved =
    sessionStorage.getItem('selectedPrivacy') || localStorage.getItem('selectedPrivacy');

  let result;
  /* parsing undefined would give an error */
  if (saved) result = JSON.parse(saved);

  /* returns true if and only if we find a saved value that's set to true
  if don't find a saved value, return undefined to reflect that */
  return result;
};

/**
 * Checks if the given time has expired as per the defined expiration period.
 *
 * This function calculates the difference between the current time and the provided
 * timestamp. If this difference is greater than the expiration period preset (one week
 * in milliseconds), the function concludes that the time has expired and returns true.
 * Otherwise, it retunes false.
 *
 * @param {number} time - The timestamp to check for expiration (in milliseconds).
 * @returns {boolean} True if the timestamp is expired, false otherwise.
 */
const isExpired = time => Date.now() - time > MILLISECONDS_IN_WEEK;

/**
 * Retrieves a value from the sessionStorage if the item exists and is not expired.
 *
 * This function fetches an item from the sessionStorage using the provided 'item' key.
 * It parses the sessionStorage item from the JSON to an Object and checks if the item
 * is expired based on its 'timeStamp' property. If the item is expired, it is removed
 * from the sessionStorage. If it is not expired, its 'value' property is stored in
 * the 'saved' variable which is then returned by the function.
 *
 * Note: This function expect the sessionStorage item to be a JSON string with 'timeStamp'
 * and 'value' properties, thus is not truly general purpose.
 *
 * @param {string} item - The key of the item in the sessionStorage.
 * @returns {string | undefined} The value of the sessionStorage item or undefined if
 * the item doesn't exist or is expired.
 */
const getSessionValue = item => {
  let saved;
  let sessionItem = sessionStorage.getItem(item);
  if (sessionItem) {
    sessionItem = JSON.parse(sessionItem);
    if (isExpired(sessionItem.timeStamp)) {
      sessionStorage.removeItem(item);
    } else {
      saved = sessionItem.value;
    }
  }
  return saved;
};

/**
 * Retrieves a value from localStorage if it exists and not expired.
 *
 * This function is designed to get items from localStorage. Once an item is retrieved,
 * the function checks the user's privacy settings with `getPrivacy()` and the item's
 * timestamp to confirm that it hasn't expired with `isExpired()`. If the item is expired,
 * it is removed from localStorage. If not, the item's value is returned.
 *
 * @param {string} item - The name of the item to retrieve from localStorage.
 * @returns {object} The value stored in localStorage. Undefined if the item does not exist or is expired.
 */
const getLocalValue = item => {
  let saved;
  let localItem = localStorage.getItem(item);
  if (localItem && getPrivacy()) {
    localItem = JSON.parse(localItem);
    if (isExpired(localItem.timeStamp)) {
      localStorage.removeItem(item);
    } else {
      saved = localItem.value;
    }
  }
  return saved;
};

/**
 * Fetches an item from either the session storage or local storage.
 *
 * This function retrieves a value from the session storage by means of the
 * getSessionValue function, If the item does not exist in session storage, it attempts to
 * retrieve the value from the local storage using getLocalValue function. If the item is
 * still missing, the defaultValue passed as argument is returned.
 *
 * @param {string} item - The key/name of the item that will be searched in the storage.
 * @param {*} defaultValue - The fallback value that will be returned if the item
 * does not exist in any storage.
 * @returns {*} Retrieved item from the storage or the default value if
 * the item does not exist in any storage.
 */
export const getItem = (item, defaultValue) =>
  getSessionValue(item) || getLocalValue(item) || defaultValue;

/**
 * Sets an item to session or local storage, depending on the privacy settings.
 *
 * This function accepts an item and value pair to be set in the session storage. If the item is
 * part of a specified list of local storage fields and privacy setting permits, the item-value
 * pair is also set in the local storage. Value is ignored if it's undefined or empty string.
 *
 * @param {string} item - The key for the storage item.
 * @param {any} value - The value to be set for the specified key/item. It can be of any data type.
 */
export const setItem = (item, value) => {
  // undefined and empty strings cause errors on JSON.parse
  if (value !== undefined && value !== '') {
    const storageItem = JSON.stringify({ timeStamp: Date.now(), value });

    sessionStorage.setItem(item, storageItem);

    if (LOCAL_STORAGE_FIELDS[item] && getPrivacy()) {
      localStorage.setItem(item, storageItem);
    }
  }
};

/**
 * Sets the selected privacy settings in local and session storage.
 *
 * This function updates the selected privacy settings in both local and session storage.
 * It also removes any local storage fields if the selectedPrivacy parameter is falsy,
 * ensuring a clean slate for new settings.
 *
 * @param {object} selectedPrivacy - The selected privacy settings to be stored,
 * represented as a key-value pair object.
 */
export const setPrivacy = selectedPrivacy => {
  sessionStorage.setItem('selectedPrivacy', JSON.stringify(selectedPrivacy));
  localStorage.setItem('selectedPrivacy', JSON.stringify(selectedPrivacy));
  if (!selectedPrivacy) {
    Object.keys(LOCAL_STORAGE_FIELDS).forEach(field => {
      localStorage.removeItem(field);
    });
  }
};

/**
 * Retrieves the 'userWasNotified' value from the session storage.
 *
 * This function checks the session storage for a flag indicating if the user has
 * been notified during the current session. It uses the 'sessionStorage.getItem' method to
 * acquire the 'userWasNotified' key. This key's value is stored as a JSON
 * string, so 'JSON.parse' method is used to convert it back to its original data type.
 *
 * @returns {boolean} Indicates whether the user was notified or not. If the key
 * doesn't exist, it returns null.
 */
export const getUserWasNotified = () => JSON.parse(sessionStorage.getItem('userWasNotified'));

/**
 * Sets the 'userWasNotified' flag in the current session storage.
 *
 * This function takes as input a boolean value and stores it in the session storage after
 * converting it into a string using JSON.stringify(). The key used for storage is
 * 'userWasNotified'. It can be used to save a status which indicates whether the user
 * was notified or not in the current browsing session.
 *
 * @param {boolean} bool - The flag indicating whether the user was notified or not.
 */
export const setUserWasNotified = bool => {
  sessionStorage.setItem('userWasNotified', JSON.stringify(bool));
};

/**
 * Sets a timestamp as a session end-time when the window is closed.
 *
 * When the window unloads (e.g., the browser tab is closed), this function sets a timestamp
 * that represents the end-time of the current session. It uses 'sessionStorage' to
 * save the timestamp, which is created using the Date.now() method. This can be useful
 * for tracking user session times.
 *
 * @returns {undefined} This function doesn't directly return a value; it sets a session
 * storage item instead.
 */
export const setTimeStampOnUnload = () => {
  window.addEventListener('unload', () => {
    sessionStorage.setItem('sessionEndTime', Date.now());
  });
};

/**
 * Updates or removes content from session storage based on previous session end time.
 *
 * If the session end time is more than three seconds ago, the function discards all items
 * in the session storage, excluding text size, contrast, and language settings.
 * This function may be used in scenarios where tracking and maintenance of session storage
 * for particular data is needed, without affecting general settings.
 *
 * @param {object} storageObject - The object containing the storage keys to be removed if
 * required.
 */
export const useOrDiscardSessionStorage = storageObject => {
  const prevEndTime = sessionStorage.getItem('sessionEndTime', Date.now());
  if (prevEndTime < Date.now() - THREE_SECONDS) {
    /* Not using sessionStorage.clear() as would rather keep text size, contrast, and language settings */
    Object.keys(storageObject).forEach(key => {
      sessionStorage.removeItem(key);
    });
    setUserWasNotified(false);
  }
};
